import * as React from 'react'
import { useEffect } from 'react'
import { useCookies } from 'react-cookie'
import { useForm } from 'react-hook-form'
import { SubmitErrorHandler } from 'react-hook-form'
import { Box, Button, InputAdornment, Link, Stack, TextField, Typography } from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Image from 'next/image'
import { useRouter } from 'next/router'
import { signIn } from 'next-auth/react'

import styles from './login.module.scss'

import ClosedEyeSvg from '#/assets/svg/closed-eye.svg?react'
import OpenedEyeSvg from '#/assets/svg/opened-eye.svg?react'
import { PasswordOptions } from '#/features/register-by-email/lib/constants'
import { YandexAuthButton } from '#/features/yandex-auth-button'
import { NextPageWithLayout } from '#/pages/_app'
import { ERROR_MAPPING, ERRROR_YANDEX_TRANSLATE_MAPPING } from '#/shared/lib/auth/constants'
import { emailOptions } from '#/shared'
import { getDeviceType } from '#/shared/lib/helpers'
import { useShowDataStore } from '#/shared/lib/hooks/use-show-data'
import { InputStyleDark, InputStyleLight } from '#/shared/ui/input'

const Login: NextPageWithLayout = () => {
	const device = getDeviceType()

	const { query, push } = useRouter()

	const [cookie, setCookie] = useCookies()

	React.useEffect(() => {
		Object.entries(query).forEach(([key, value]) => setCookie(key, value))
	}, [query])

	const { showMessage } = useShowDataStore()

	const [loading, setLoading] = React.useState(false)

	const [passwordShowed, showPassword] = React.useState(false)

	const desktop = device === 'desktop'

	const { register, handleSubmit } = useForm()

	useEffect(() => {
		const params = new URL(window.location.href).searchParams

		const error = params.get('error')

		if (!error || error === '') return

		const yandexError = ERRROR_YANDEX_TRANSLATE_MAPPING[error]
		if (yandexError) {
			showMessage(yandexError)
			return
		}

		const authError = Object.fromEntries(
			Object.entries(ERROR_MAPPING).map(([message, slug]) => [slug, message])
		)[error]
		if (authError) {
			showMessage(authError)
			return
		}

		showMessage(error)
	}, [])

	async function onSubmit(data: Record<string, string>) {
		setLoading(true)

		const resp = await signIn('credentials', {
			username: data.email,
			password: data.password,
			redirect: false,
		})
		if (resp && !resp.ok && resp.error && resp.error !== '') {
			const expectedError = Object.keys(ERROR_MAPPING).reduce(
				(prev, curr) => ({ ...prev, [ERROR_MAPPING[curr]]: curr }),
				{} as Record<string, string>
			)[resp.error]
			showMessage(expectedError ?? 'Не удалось выполнить вход, попробуйте позже')
			setLoading(false)
		} else {
			push('/')
		}
	}

	const checkError: SubmitErrorHandler<any> = (data) => {
		const first = Object.values(data)[0] as { message?: string } | undefined
		showMessage(first?.message || 'Неверные данные')
	}

	const handleKeyDown = (event: any) => {
		if (event.key === 'Enter') {
			handleSubmit(onSubmit)()
		}
	}

	const { ref: emailInputRef, onChange: emailOnChange, ...emailRegister } = register(
		'email',
		emailOptions
	)
	const { ref: passwordInputRef, ...passwordRegister } = register('password', {
		...PasswordOptions,
	})

	const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		event.target.value = event.target.value.trim()
		emailOnChange(event)
	}

	return (
		<>
			<Stack
				className={styles.container}
				direction={desktop ? 'row' : 'column'}
				alignItems='center'
				justifyContent={desktop ? 'space-between' : 'center'}
				sx={{
					overflow: 'hidden',
					padding: 0,
					margin: desktop ? 0 : '0px auto',
					backgroundColor: '#303035',
				}}
			>
				{!desktop && (
					<Image
						className={styles.logoMobile}
						src={'/logo.svg'}
						alt={''}
						width={50}
						height={50}
					/>
				)}
				<Box
					className={styles.form}
					sx={{
						width: desktop ? '50%' : '100%',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
				>
					<form className={styles.loginForm} onKeyDown={handleKeyDown} autoComplete='on'>
						<Box
							className={styles.formInner}
							sx={{
								marginTop: 0,
							}}
							onKeyDown={async (e) => {
								if (e.key === 'Enter') {
									await handleSubmit(onSubmit, checkError)
								}
							}}
						>
							<Stack
								direction='column'
								justifyContent='center'
								alignItems='center'
								spacing={1}
							>
								<Typography
									sx={{
										color: '#E1E1E1',
										lineHeight: '36.4px',
										fontSize: '26px',
										fontWeight: 'bold',
										marginBottom: '20px',
									}}
								>
									Вход
								</Typography>
								<YandexAuthButton />
								<Typography
									variant='body2'
									sx={{
										color: '#A4AAB5',
										lineHeight: '19.6px',
										fontSize: '14px',
										paddingTop: 1,
										paddingRight: '90%',
									}}
								>
									Email
								</Typography>
								<TextField
									fullWidth
									sx={{ ...InputStyleDark }}
									inputRef={emailInputRef}
									autoComplete='username'
									{...emailRegister}
									onChange={handleEmailChange}
								/>

								<Box
									sx={{
										width: '100%',
										justifyContent: 'space-between',
										display: 'flex',
									}}
								>
									<Box>
										<Typography
											variant='body2'
											display='inline'
											sx={{
												color: '#A4AAB5',
												lineHeight: '19.6px',
												fontSize: '14px',
											}}
										>
											Пароль
										</Typography>
									</Box>
									<Box>
										<Typography
											variant='body2'
											display='inline'
											sx={{
												color: '#8153FB',
												lineHeight: '19.6px',
												fontSize: '14px',
												fontWeight: '500',
											}}
										>
											<Link href='/reset' className={styles.linkAccent}>
												Забыли пароль?
											</Link>
										</Typography>
									</Box>
								</Box>
								<TextField
									type={passwordShowed ? 'text' : 'password'}
									fullWidth
									InputProps={{
										endAdornment: (
											<InputAdornment position='start'>
												<div
													onClick={() =>
														showPassword((x) => !x)
													}
													className={styles.passwordToggle}
												>
													{passwordShowed ? (
														<ClosedEyeSvg
															width={24}
															height={24}
															color='grey'
														/>
													) : (
														<OpenedEyeSvg
															width={24}
															height={24}
															color='grey'
														/>
													)}
												</div>
											</InputAdornment>
										),
									}}
									sx={{ ...InputStyleDark }}
									inputRef={passwordInputRef}
									autoComplete='current-password'
									{...passwordRegister}
								/>
								<Box sx={{ height: '50px', width: '100%' }}>
									{!loading ? (
										<Button
											variant='contained'
											className={styles.loginSubmitButton}
											sx={{
												backgroundColor: '#7F7DF3',
												color: '#FFFFFF',
												lineHeight: '20px',
												fontSize: '16px',
												fontWeight: '600px',
												borderRadius: '13px',
												textTransform: 'none',
												width: '100%',
												height: 48,
												'&:hover': {
													backgroundColor: '#7F7DF3',
													opacity: [0.9, 0.8, 0.7],
												},
											}}
											onClick={handleSubmit(onSubmit, checkError)}
										>
											Войти
										</Button>
									) : (
										<Box className={styles.loginLoader}>
											<CircularProgress
												size={30}
												thickness={5}
												sx={{ color: '#7F7DF3' }}
											/>
										</Box>
									)}
									<Box
										sx={{
											width: '100%',
											textAlign: 'center',
											marginTop: '15px',
										}}
									>
										<Link href='/register' className={styles.linkAccent}>
											Зарегистрироваться
										</Link>
									</Box>
								</Box>
							</Stack>
						</Box>
					</form>
				</Box>
				{desktop && (
					<Box
						className={styles.imagebox}
						sx={{
							width: '50%',
							height: '100vh',
							backgroundColor: '#8280FF',
							overflow: 'hidden',
							position: 'relative',
						}}
					>
						<Image
							className={styles.imageboxLogo}
							src={'/svg/logo.svg'}
							alt={''}
							width={42}
							height={42}
						/>
						<Image
							src={'/images/dashboard.png'}
							alt={''}
							width={900}
							height={1002}
							className={styles.promoDashboardImage}
						/>
					</Box>
				)}
			</Stack>
		</>
	)
}
export default Login
